/**
 * Classe de base abstraite pour tous les widgets Apidae
 * Contient la logique commune de chargement de données et de gestion d'état
 */

import { LitElement, PropertyValues, unsafeCSS, html } from "lit";
import { property, state } from "lit/decorators.js";
import baseWidgetStyles from "../styles/base-widget.css?inline";
import {
  WidgetConfiguration,
  WidgetLanguage,
  FilterId,
  FILTER_DEFINITIONS,
} from "../types/widgets.d";
import { ObjetTouristique, FacetId, FacetValue } from "../types/apidae.d";
import { WidgetService } from "../services/WidgetService";
import { StyleService } from "../services/StyleService";
import { ToastHandler, ToastIntent } from "../types/toastOptions.d";
import { FilterGroup } from "../types/panel.d";

import {
  MOCK_OBJETS_TOURISTIQUES,
  MOCK_WIDGET_CONFIG,
  MOCK_WIDGET_FILTERS,
} from "../mockData";
import "./DetailView";
import apidaeLogo from "../assets/apidae-logo.svg";
import apidaeLogoText from "../assets/apidae-logo-text.svg";
import { LayoutFraction } from "./WidgetLayout";
import { t, getApiUrlFromEnvironment } from "../utils/widgetsUtils";

export abstract class BaseWidget extends LitElement {
  // Propriétés publiques (attributs HTML)
  @property({ type: String, attribute: "widget-id" })
  widgetId: string = "";

  @property({ type: String, attribute: "api-url" })
  apiUrl: string = "";

  @property({ type: Boolean, attribute: "use-mock" })
  useMock: boolean = false;

  // Propriétés publiques (attributs HTML)
  @property({ type: Boolean, attribute: "preview" })
  modePreview: boolean = false;

  // Indique si le widget est utilisé comme enfant dans un widget de composition
  @property({ type: Boolean, attribute: "isChild" })
  isChildWidget: boolean = false;

  // État interne
  @state()
  protected config: WidgetConfiguration | null = null;

  protected previousConfig: WidgetConfiguration | null = null;

  @state()
  protected items: ObjetTouristique[] = [];

  @state()
  protected loading: boolean = true;

  @state()
  protected error: string | null = null;

  @state()
  protected totalItems: number = 0;

  @state()
  protected hasMore: boolean = false;

  @state()
  protected currentPage: number = 1;

  protected itemsPerPage: number = 10;

  @state()
  protected detailItem: ObjetTouristique | null = null;

  @state()
  private toastMessage: string | null = null;
  @state()
  private toastIntent: ToastIntent = "info";

  // Filtres depuis l'API
  @state()
  protected apiFilters: Map<FacetId, FacetValue[]> = new Map();

  // Dates pour le filtre OUVERTURE
  @state()
  protected dateRange: { dateDebut: string | null; dateFin: string | null } = {
    dateDebut: null,
    dateFin: null,
  };

  // Filtres actifs (définis dans les widgets enfants mais accessible depuis BaseWidget)
  @state()
  protected activeFilters: Map<FilterId, Set<string>> = new Map();

  // Services
  protected widgetService: WidgetService;
  protected language: WidgetLanguage = WidgetLanguage.FR;

  // Flags pour éviter les appels multiples
  private isLoadingData: boolean = false;
  private isReloadingFilters: boolean = false;

  @state()
  protected filterGroups: FilterGroup[] = [];

  /** Nombre d'éléments demandés par facet (FacetId -> count). 11 initial, puis +10 à chaque "voir plus". */
  @state()
  protected facetsConfig: Record<string, number> = {};

  /** Facet en cours de chargement "tout" (boucle voir plus). Empêche double clic. */
  @state()
  protected loadingMoreFacetId: FacetId | null = null;

  /** Une fois true, on a déclenché le chargement complet des communes ; reset au changement de filtres. */
  private communesLoadAllTriggered: boolean = false;

  @state()
  protected filtersVisible: boolean = false;

  @property({ type: Boolean, attribute: "hide-header-and-filters" })
  hideHeaderAndFilters: boolean = false;

  @property({ type: Boolean, attribute: "hide-empty-state" })
  hideEmptyState: boolean = false;

  @property({ type: Number, attribute: false })
  selectedItemId?: number | null = null;

  @property({ type: Boolean, attribute: false })
  disableDetailView: boolean = false;

  constructor() {
    super();
  }

  /**
   * Affiche un toast
   */
  private showToast: ToastHandler = (
    message: string,
    intent: ToastIntent = "info",
    duration: number = 3000,
  ) => {
    this.toastMessage = message;
    this.toastIntent = intent;
    this.requestUpdate();

    // Masquer automatiquement après la durée spécifiée
    setTimeout(() => {
      this.toastMessage = null;
      this.requestUpdate();
    }, duration);
  };

  /**
   * Met à jour la langue dans les services
   */
  private updateServicesLanguage(): void {
    // Recréer les services avec la nouvelle langue
    this.initializeServices();
  }

  /**
   * Initialise les services avec la configuration actuelle
   */
  private initializeServices(): void {
    const timeout = 10000;

    // Utiliser en priorité l'URL fournie via la prop, sinon fallback sur l'environnement
    const resolvedApiUrl =
      this.apiUrl && this.apiUrl.trim() !== ""
        ? this.apiUrl
        : getApiUrlFromEnvironment();

    this.widgetService = new WidgetService({
      apiUrl: resolvedApiUrl,
      timeout,
      toastHandler: this.showToast,
      language: this.language,
    });
  }

  /**
   * Styles communs à tous les widgets
   */
  static get sharedStyles() {
    return unsafeCSS(baseWidgetStyles);
  }

  connectedCallback() {
    super.connectedCallback();
    // Initialiser les services une fois que les attributs HTML (dont api-url) sont appliqués
    this.initializeServices();
    // En mode preview avec config injectée, ne pas charger depuis l'API
    if (this.modePreview && this.config) {
      // La config est déjà injectée, on initialise juste les services
      this.language = this.config.contentCustomization.language;
      this.updateServicesLanguage();
      this.applyCustomStyles();
      // Si des items sont injectés, on les utilise directement
      if (this.items && this.items.length > 0) {
        this.loading = false;
      }
    } else if (this.useMock) {
      this.loadMockData();
    } else {
      // Les widgets enfants ne chargent pas de données, ils attendent que le parent les synchronise
      if (this.isChildWidget) {
        this.loading = false; // Afficher le contenu tout de suite (items/config fournis par le parent)
      } else {
        this.loadWidget();
      }
    }
  }

  changedNotNull(changedProperties: PropertyValues, name: string): boolean {
    const value = changedProperties.get(name);
    return changedProperties.has(name) && value !== null && value !== undefined;
  }

  updated(changedProperties: PropertyValues) {
    let hasVisualChange = false;
    let hasLanguageChange = false;
    let hasDataChange = false;

    if (
      this.changedNotNull(changedProperties, "config") &&
      this.config !== null &&
      this.config !== this.previousConfig
    ) {
      hasVisualChange = this.hasConfigVisualChange(
        this.previousConfig,
        this.config,
      );
      hasLanguageChange = this.hasConfigLanguageChange(
        this.previousConfig,
        this.config,
      );
      hasDataChange = this.hasConfigDataChange(
        this.previousConfig,
        this.config,
      );
      this.previousConfig = this.config;
    }

    // Réagir aux changements de useMock
    if (
      this.changedNotNull(changedProperties, "useMock") &&
      this.useMock &&
      !this.widgetId &&
      !this.config
    ) {
      this.loadMockData();
      return; // Éviter de continuer si on charge les données mock
    }

    // Réagir aux changements d'ID ou d'URL
    const hasWidgetIdChange = this.changedNotNull(
      changedProperties,
      "widgetId",
    );
    const hasApiUrlChange = this.changedNotNull(changedProperties, "apiUrl");
    const filterChange = this.changedNotNull(
      changedProperties,
      "activeFilters",
    );

    // Si l'URL de l'API change, réinitialiser les services pour utiliser la nouvelle baseURL
    if (hasApiUrlChange) {
      this.initializeServices();
    }

    if (hasWidgetIdChange || hasApiUrlChange || filterChange) {
      hasDataChange = true;
    }

    // Quand le parent définit isChildWidget (ex: carte dans fiche détail), annuler erreur/chargement
    if (
      this.changedNotNull(changedProperties, "isChildWidget") &&
      this.isChildWidget
    ) {
      this.error = null;
      this.loading = false;
    }

    // Réagir aux changements de config injectée (mode preview)
    // Ne réinitialiser loading et error que si nécessaire
    if (hasDataChange) {
      this.loading = false;
      this.error = null;
    }

    // Réinitialiser les services si l'URL de l'API change
    if (hasApiUrlChange) {
      this.initializeServices();
    }

    if (hasLanguageChange) {
      this.language =
        this.config?.contentCustomization.language || WidgetLanguage.FR;
      // Mettre à jour les services avec la nouvelle langue
      this.updateServicesLanguage();
    }

    if (hasVisualChange) {
      // Appliquer les styles personnalisés
      this.applyCustomStyles();
    }

    if (hasDataChange && !this.isLoadingData && !this.isChildWidget) {
      // Charger les données seulement si on n'est pas déjà en train de charger
      // et si on n'est pas un widget enfant (les widgets enfants émettent des événements au lieu de faire des appels API)
      this.loadData();
    }
  }

  private hasConfigVisualChange(
    previousConfig: WidgetConfiguration | null,
    config: WidgetConfiguration | null,
  ): boolean {
    return (
      previousConfig?.name !== config?.name ||
      previousConfig?.visualCustomization !== config?.visualCustomization ||
      previousConfig?.templateSpecificConfig !== config?.templateSpecificConfig
    );
  }

  private hasConfigLanguageChange(
    previousConfig: WidgetConfiguration | null,
    config: WidgetConfiguration | null,
  ): boolean {
    return (
      previousConfig?.contentCustomization.language !==
      config?.contentCustomization.language
    );
  }

  private hasConfigDataChange(
    previousConfig: WidgetConfiguration | null,
    config: WidgetConfiguration | null,
  ): boolean {
    return (
      previousConfig?.selections !== config?.selections ||
      previousConfig?.targetUrl !== config?.targetUrl ||
      previousConfig?.projetId !== config?.projetId ||
      config?.contentCustomization?.filterFields !==
        previousConfig?.contentCustomization?.filterFields ||
      config?.contentCustomization?.aspectId !==
        previousConfig?.contentCustomization?.aspectId ||
      config?.contentCustomization?.displayedFields !==
        previousConfig?.contentCustomization?.displayedFields ||
      config?.contentCustomization?.displayedFieldsPreview !==
        previousConfig?.contentCustomization?.displayedFieldsPreview ||
      config?.contentCustomization?.excludedOfferIds !==
        previousConfig?.contentCustomization?.excludedOfferIds ||
      config?.contentCustomization?.geographicPerimeter !==
        previousConfig?.contentCustomization?.geographicPerimeter ||
      config?.contentCustomization?.language !==
        previousConfig?.contentCustomization?.language
    );
  }

  /**
   * Applique les styles personnalisés au widget
   */
  private applyCustomStyles(): void {
    if (!this.config?.visualCustomization) return;

    // Supprimer l'ancien élément de style s'il existe
    const existingStyle = this.shadowRoot?.querySelector("#custom-styles");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Créer un nouvel élément style
    const styleElement = document.createElement("style");
    styleElement.id = "custom-styles";

    // Générer les variables CSS
    const cssVariables = StyleService.generateCSSVariables(
      this.config.visualCustomization,
    );

    // Ajouter le CSS personnalisé s'il existe
    const customCSS = this.config.visualCustomization.customCSS || "";

    styleElement.textContent = `
      :host {
        ${cssVariables}
      }
      ${customCSS}
    `;

    // Injecter dans le shadow root
    this.shadowRoot?.appendChild(styleElement);

    // Charger les polices Google Fonts si nécessaire
    if (
      this.config?.visualCustomization.typography.textFont ||
      this.config?.visualCustomization.typography.titleFont
    ) {
      StyleService.loadGoogleFonts([
        this.config.visualCustomization.typography.textFont,
        this.config.visualCustomization.typography.titleFont,
      ]);
    }
  }

  /**
   * Charge la configuration et les données du widget
   */
  private async loadWidget(): Promise<void> {
    // En mode enfant (ex: carte dans fiche détail), ne rien charger — les données viennent du parent.
    // Garde nécessaire car connectedCallback peut s'exécuter avant que le parent n'ait défini isChildWidget.
    if (this.isChildWidget) {
      return;
    }
    if (!this.widgetId) {
      this.error = "ID du widget manquant";
      this.loading = false;
      return;
    }

    // Éviter les appels multiples
    if (this.isLoadingData || this.isReloadingFilters) {
      return;
    }

    if (this.useMock) {
      this.loadMockData();
      return;
    }

    try {
      this.loading = true;
      this.error = null;

      // Charger la configuration si elle n'est pas passée en paramètre
      if (!this.config) {
        this.config = await this.widgetService.getWidgetConfig(this.widgetId);
      }

      this.language = this.config.contentCustomization.language;

      // Mettre à jour les services avec la nouvelle langue
      this.updateServicesLanguage();

      // Appliquer les styles personnalisés
      this.applyCustomStyles();

      // Charger les filtres depuis l'API (sans dates initialement)
      try {
        // Inclure systématiquement le français dans les locales
        const locales = [this.language];
        if (this.language !== WidgetLanguage.FR) {
          locales.push(WidgetLanguage.FR);
        }

        this.loadData();
      } catch (filterError) {
        console.warn(
          "Impossible de charger les filtres depuis l'API, utilisation des filtres par défaut:",
          filterError,
        );
        this.apiFilters = new Map();
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Erreur de chargement";
      this.loading = false;
      console.error("Erreur lors du chargement du widget:", err);
    }
  }

  /**
   * Charge une page de données mock avec pagination
   */
  private loadMockDataPage(page: number): void {
    const start = (page - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    // Si première page, remplacer les items, sinon ajouter
    this.items =
      page === 1
        ? MOCK_OBJETS_TOURISTIQUES.slice(start, end)
        : [...this.items, ...MOCK_OBJETS_TOURISTIQUES.slice(start, end)];
    this.totalItems = MOCK_OBJETS_TOURISTIQUES.length;
    this.hasMore = end < MOCK_OBJETS_TOURISTIQUES.length;
    this.currentPage = page;
  }

  /**
   * Charge une page de données mock en remplaçant les items existants
   * Utile pour les widgets qui ne font pas de scroll infini (comme le carousel)
   */
  protected loadMockDataPageAndReplace(page: number): void {
    const start = (page - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    // Toujours remplacer les items
    this.items = MOCK_OBJETS_TOURISTIQUES.slice(start, end);
    this.totalItems = MOCK_OBJETS_TOURISTIQUES.length;
    this.hasMore = end < MOCK_OBJETS_TOURISTIQUES.length;
    this.currentPage = page;
  }

  /**
   * Charge les données mock directement (mode démo sans config)
   */
  private async loadMockData(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;

      // Simuler un délai de chargement
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Charger la configuration mock
      this.config = { ...MOCK_WIDGET_CONFIG };
      this.language = this.config.contentCustomization.language;

      // Mettre à jour les services avec la nouvelle langue
      this.updateServicesLanguage();

      // Appliquer les styles personnalisés
      this.applyCustomStyles();

      // Charger les filtres mock
      this.apiFilters = MOCK_WIDGET_FILTERS;
      this.filterGroups = this.convertApiFiltersToFilterGroups();

      // Charger les données mock avec pagination
      this.loadMockDataPage(1);
      this.loading = false;
    } catch (err) {
      this.error =
        err instanceof Error
          ? err.message
          : "Erreur de chargement des données mock";
      this.loading = false;
      console.error("Erreur lors du chargement des données mock:", err);
    }
  }

  /**
   * Construit la config initiale des facets (11 éléments par facet) à partir des filterFields.
   * Utilisée au premier chargement ; ensuite facetsConfig n'est modifié que par "voir plus".
   */
  private buildInitialFacetsConfig(): Record<string, number> {
    const config: Record<string, number> = {};
    const fields = this.config?.contentCustomization.filterFields || [];
    for (const f of fields) {
      if (f.id === FilterId.OUVERTURE) continue;
      const def = FILTER_DEFINITIONS[f.id as FilterId];
      if (def?.facet) {
        config[def.facet] = 11;
      }
    }
    return config;
  }

  /**
   * Retourne la facetsConfig à envoyer à l'API (initial 11 si jamais défini, sinon état actuel).
   */
  private getFacetsConfigForRequest(): Record<string, number> {
    if (Object.keys(this.facetsConfig).length > 0) {
      return this.facetsConfig;
    }
    return this.buildInitialFacetsConfig();
  }

  /**
   * Demande toutes les options pour une facet : boucle +10 jusqu'à en avoir tout (un seul clic déclenche la boucle).
   */
  protected async loadMoreForFacet(facetId: FacetId): Promise<void> {
    if (!this.config || this.isChildWidget || this.loadingMoreFacetId) return;
    this.loadingMoreFacetId = facetId;
    try {
      let group: FilterGroup | undefined;
      do {
        const current = this.facetsConfig[facetId] ?? 11;
        this.facetsConfig = { ...this.facetsConfig, [facetId]: current + 10 };
        await this.reloadFacetsOnly();
        group = this.filterGroups.find((g) => g.facet === facetId);
      } while (group?.hasMore);
    } finally {
      this.loadingMoreFacetId = null;
    }
  }

  /**
   * Recharge uniquement les facets (0 OT) avec la facetsConfig actuelle. N'affecte pas items/totalItems.
   */
  protected async reloadFacetsOnly(): Promise<void> {
    if (!this.config || this.isLoadingData || this.useMock) return;
    this.isLoadingData = true;
    try {
      const result = await this.widgetService.getWidgetData(
        this.modePreview,
        this.widgetId,
        this.config,
        1,
        this.itemsPerPage,
        this.dateRange.dateDebut || undefined,
        this.dateRange.dateFin || undefined,
        undefined,
        undefined,
        undefined,
        this.activeFilters,
        this.getFacetsConfigForRequest(),
        true, // facetsOnly
      );
      this.apiFilters = this.convertFacetsToMap(result.facets);
      this.filterGroups = this.convertApiFiltersToFilterGroups();
    } catch (err) {
      console.error("Erreur lors du rechargement des facets:", err);
    } finally {
      this.isLoadingData = false;
    }
  }

  /**
   * Charge les données depuis l'API ou utilise les données mock
   * @param showLoading Afficher l'écran de chargement (par défaut true)
   */
  protected async loadData(
    page: number = 1,
    showLoading: boolean = true,
    center?: { type: "Point"; coordinates: [number, number] },
    radius?: number,
  ): Promise<void> {
    if (!this.config || this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;

    try {
      if (showLoading) {
        this.loading = true;
      }
      this.error = null;

      if (this.useMock) {
        // Simuler la pagination avec les données mock
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.loadMockDataPage(page);
        // Appliquer les filtres AVANT de notifier (pour que filteredItems soit à jour)
        this.currentPage = page;
      } else {
        // Au premier chargement (page 1), initialiser facetsConfig si vide
        if (page === 1 && Object.keys(this.facetsConfig).length === 0) {
          this.facetsConfig = this.buildInitialFacetsConfig();
        }
        const facetsConfigToSend = this.getFacetsConfigForRequest();

        const result = await this.widgetService.getWidgetData(
          this.modePreview,
          this.widgetId,
          this.config,
          page,
          this.itemsPerPage,
          this.dateRange.dateDebut || undefined,
          this.dateRange.dateFin || undefined,
          undefined, // identifiants
          center,
          radius,
          this.activeFilters,
          facetsConfigToSend,
          false, // facetsOnly
        );

        // Si première page, remplacer les items, sinon ajouter
        const wasFirstPage = page === 1;

        // Convertir l'objet JSON facets en Map<FacetId, FacetValue[]>
        this.apiFilters = this.convertFacetsToMap(result.facets);
        this.filterGroups = this.convertApiFiltersToFilterGroups();

        // Communes : charger toutes les valeurs en arrière-plan (combobox avec tout, pas de bouton Voir plus).
        // Décaler après la fin de loadData pour que reloadFacetsOnly() ne soit pas bloqué par isLoadingData.
        if (
          page === 1 &&
          !this.communesLoadAllTriggered &&
          !this.useMock &&
          !this.isChildWidget
        ) {
          const communesGroup = this.filterGroups.find(
            (g) => g.id === FilterId.COMMUNES && g.facet,
          );
          if (communesGroup?.hasMore && communesGroup.facet) {
            this.communesLoadAllTriggered = true;
            const facetId = communesGroup.facet;
            queueMicrotask(() => this.loadMoreForFacet(facetId));
          }
        }

        this.items =
          page === 1 ? result.items : [...this.items, ...result.items];
        this.totalItems = result.total;
        this.hasMore = result.hasMore;

        // Appliquer les filtres AVANT de notifier (pour que filteredItems soit à jour)
        this.currentPage = page;
        // Notifier que les données ont été chargées (méthode à surcharger dans les widgets de composition)
        this.notifyDataLoaded({
          items: this.items,
          totalItems: this.totalItems,
          hasMore: this.hasMore,
          page: page,
          isFirstPage: wasFirstPage,
          center: center,
          radius: radius,
        });
      }
      if (showLoading) {
        this.loading = false;
      }
    } catch (err) {
      console.error("Erreur lors du chargement des données:", err);
      this.error =
        err instanceof Error ? err.message : "Erreur de chargement des données";
      if (page === 1) {
        this.items = [];
        this.totalItems = 0;
        this.hasMore = false;
      }
      if (showLoading) {
        this.loading = false;
      }
    } finally {
      this.isLoadingData = false;
    }
  }

  /**
   * Charge la page suivante
   */
  protected async loadNextPage(showLoading: boolean = false): Promise<void> {
    if (!this.hasMore || this.loading) {
      return;
    }
    await this.loadData(this.currentPage + 1, showLoading);
  }

  /**
   * Convertit les facets de l'API (objet JSON ou Map) en Map<FacetId, FacetValue[]>
   */
  protected convertFacetsToMap(
    facets:
      | Map<FacetId, FacetValue[]>
      | Record<string, FacetValue[]>
      | undefined,
  ): Map<FacetId, FacetValue[]> {
    const facetsMap = new Map<FacetId, FacetValue[]>();

    if (!facets) {
      return facetsMap;
    }

    // Si c'est déjà une Map, la retourner telle quelle
    if (facets instanceof Map) {
      return facets;
    }

    // Sinon, convertir l'objet JSON en Map
    Object.entries(facets).forEach(([key, values]) => {
      // Vérifier que la clé est une valeur valide de FacetId
      if (Object.values(FacetId).includes(key as FacetId)) {
        facetsMap.set(key as FacetId, values);
      }
    });

    return facetsMap;
  }

  /**
   * Convertit les filtres de l'API en FilterGroup[] pour le FilterPanel
   */
  protected convertApiFiltersToFilterGroups(): FilterGroup[] {
    if (this.apiFilters.size === 0) {
      return [];
    }

    const filterGroups: FilterGroup[] = [];

    for (const filter of this.config?.contentCustomization.filterFields || []) {
      const filterDefinition = FILTER_DEFINITIONS[filter.id as FilterId];
      // OUVERTURE est géré par DatePicker (pas de valeurs/options)
      if (filter.id === FilterId.OUVERTURE) {
        // Créer un FilterGroup vide (il sera géré par DatePicker dans FilterPanel)
        filterGroups.push({
          id: filter.id,
          label: this.getFilterLabel(filter.id),
          options: [],
          multiple: false,
        });
        continue;
      }

      if (filterDefinition) {
        const facet = filterDefinition.facet;
        const options = this.apiFilters.get(facet) || [];
        const requested = this.facetsConfig[facet] ?? 11;
        const hasMore = options.length >= 11 && options.length === requested;
        filterGroups.push({
          id: filter.id,
          facet,
          label: this.getFilterLabel(filter.id),
          options: options
            .map((option) => ({
              id: option.val,
              label: option.label || "",
              count: option.count || 0,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          multiple: true,
          hasMore,
        });
      }
    }

    return filterGroups;
  }

  /**
   * Récupère le libellé traduit d'un filtre
   */
  protected getFilterLabel(filterId: string): string {
    const filterLabels: Record<string, string> = {
      duree: t("duration", this.language) || "Durée",
      gratuite: t("free", this.language) || "Gratuit",
      animaux: t("animals", this.language) || "Animaux",
      classement: t("classification", this.language) || "Classement",
      communes: t("commune", this.language) || "Commune",
      activites: t("activities", this.language) || "Activités",
      types: t("type", this.language) || "Type",
      public: t("audience", this.language) || "Public",
      labels: t("labels", this.language) || "Labels",
      distance: t("distance", this.language) || "Distance",
      ouverture: t("opening", this.language) || "Ouverture",
    };
    return filterLabels[filterId] || filterId;
  }

  protected handleLoadMoreFacet(e: CustomEvent<{ facetId: FacetId }>): void {
    e.stopPropagation();
    this.loadMoreForFacet(e.detail.facetId);
  }

  protected async handleFilterChange(e: CustomEvent) {
    this.activeFilters = e.detail.filters;
    const dateRange = e.detail.dateRange;
    if (dateRange) {
      this.dateRange = dateRange;
    }
    this.communesLoadAllTriggered = false;

    // Si c'est un widget enfant, ne pas faire d'appel API (le parent gère les appels)
    if (this.isChildWidget) {
      return;
    }

    // Recharger les données depuis l'API avec les nouveaux filtres en arrière-plan (sans écran de chargement)
    this.loadData(1, false).catch((error) => {
      console.error("Erreur lors du rechargement des données:", error);
    });
  }

  protected async handleClearFilters() {
    this.activeFilters = new Map();
    this.dateRange = { dateDebut: null, dateFin: null };
    this.communesLoadAllTriggered = false;
    const filterPanel = this.shadowRoot?.querySelector(
      "apidae-filter-panel",
    ) as any;
    if (filterPanel && filterPanel.clearFilters) {
      filterPanel.clearFilters();
    }

    // Si c'est un widget enfant, ne pas faire d'appel API (le parent gère les appels)
    if (this.isChildWidget) {
      return;
    }

    // Recharger les données depuis l'API avec les filtres effacés (sans écran de chargement)
    this.loadData(1, false).catch((error) => {
      console.error("Erreur lors du rechargement des données:", error);
      // En cas d'erreur, on garde les résultats filtrés côté client
    });
  }

  protected handleToggleFilters(e: CustomEvent) {
    this.filtersVisible = e.detail.visible;
  }

  protected hasActiveFilters(): boolean {
    const hasFilterValues =
      this.activeFilters.size > 0 &&
      Array.from(this.activeFilters.values()).some(
        (set: Set<string>) => set.size > 0,
      );
    const hasDates = !!(this.dateRange.dateDebut || this.dateRange.dateFin);
    return hasFilterValues || hasDates;
  }

  /**
   * Gère le clic sur un item : affiche la vue détaillée et transmet l'item (déjà chargé dans le listing).
   */
  protected handleItemClick(item: ObjetTouristique): void {
    this.detailItem = item;

    this.dispatchEvent(
      new CustomEvent("item-click", {
        detail: { item },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Ferme la vue détaillée
   */
  protected handleCloseDetail(): void {
    this.detailItem = null;
  }

  protected getLayoutFraction(): LayoutFraction {
    const hasFilterFields =
      this.config?.contentCustomization?.filterFields &&
      this.config.contentCustomization.filterFields.length > 0;
    const showFilters =
      hasFilterFields &&
      (this.config?.templateSpecificConfig as any)?.config?.showFilters !==
        false &&
      this.filtersVisible;
    return showFilters ? "filters-content" : "full";
  }

  /**
   * Génère un dégradé CSS basé sur la couleur principale
   */
  protected getGradientFromPrimaryColor(): string {
    const primaryColor =
      this.config?.visualCustomization?.colors?.primary || "#168ac8";

    // Fonction pour assombrir une couleur
    const darken = (color: string, percent: number): string => {
      const num = parseInt(color.replace("#", ""), 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.max((num >> 16) - amt, 0);
      const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
      const B = Math.max((num & 0x0000ff) - amt, 0);
      return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
    };

    const darkerColor = darken(primaryColor, 20);
    return `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`;
  }

  /**
   * Rend le fallback pour une image manquante (logo ou dégradé)
   */
  protected renderImageFallback(altText: string = ""): any {
    const logo = this.config?.visualCustomization?.logo;

    if (logo) {
      return html`<img
        class="card-image"
        src="${logo}"
        alt="${altText || "Logo"}"
        style="object-fit: contain; background: var(--widget-card-background-color, #ffffff);"
      />`;
    }

    return html`<div
      class="card-image"
      style="background: ${this.getGradientFromPrimaryColor()};"
    ></div>`;
  }

  /**
   * Rend le header du widget avec logo et titre
   */
  protected renderHeader() {
    if (!this.config?.name) return null;

    const logo = this.config.visualCustomization?.logo;

    return html`
      <div class="widget-header">
        ${logo
          ? html`<img class="widget-logo" src="${logo}" alt="Logo" />`
          : ""}
        <h2 class="widget-title">${this.config.name}</h2>
      </div>
    `;
  }

  /**
   * Rend la vue détaillée si un item est sélectionné
   */
  protected renderDetailView() {
    if (!this.detailItem) return null;

    const displayedFields =
      this.config?.contentCustomization?.displayedFields || [];

    return html`
      <apidae-detail-view
        .item=${this.detailItem}
        .language=${this.language}
        .displayedFields=${displayedFields}
        .widgetConfig=${this.config ?? null}
        @close=${this.handleCloseDetail}
      ></apidae-detail-view>
    `;
  }

  /**
   * Rend le toast si un message est présent
   * À appeler dans la méthode render() des widgets enfants
   */
  protected renderToast() {
    if (!this.toastMessage) return null;

    return html`
      <div class="toast toast-${this.toastIntent}">${this.toastMessage}</div>
    `;
  }

  /**
   * Affiche l'écran d'erreur personnalisé
   */
  protected renderErrorScreen() {
    return html`
      <div class="error-screen">
        <div class="error-content">
          <div class="error-title">
            ${t("widgetErrorTitle", this.language) ||
            "Erreur de récupération du widget Apidae."}
          </div>
          <div class="error-description">
            ${t("widgetErrorDescription", this.language) ||
            "Ce widget n'est pas autorisé pour ce site internet. Veuillez modifier sa configuration ou modifier son intégration."}
          </div>
        </div>
        <div class="error-footer">
          <img src="${apidaeLogo}" alt="Apidae" class="error-logo" />
          <img src="${apidaeLogoText}" alt="apidae" class="error-logo-text" />
        </div>
      </div>
    `;
  }

  /**
   * Méthode helper pour le rendu de base du widget
   * Les widgets enfants peuvent l'utiliser dans leur render()
   */
  protected renderBase(content: unknown) {
    return html`
      <div class="widget-container">
        ${this.renderHeader()}
        <div class="widget-content">${content}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }

  /**
   * Notifie que les données ont été chargées
   * Méthode à surcharger dans les widgets de composition pour mettre à jour les enfants
   * @param _data Les données chargées
   */
  protected notifyDataLoaded(_data: {
    items: ObjetTouristique[];
    totalItems: number;
    hasMore: boolean;
    page: number;
    isFirstPage: boolean;
    center?: { type: "Point"; coordinates: [number, number] };
    radius?: number;
  }): void {
    // Par défaut, ne rien faire
    // À surcharger dans les widgets de composition (ex: ListMapWidget)
  }

  /**
   * Méthode abstraite à implémenter par les widgets enfants
   */
  protected abstract renderWidget(): unknown;
}
